import React, { useState } from 'react';
import DeleteSvg from '@icons/event-management/delete-blue.svg';
import {
  EventWrapper,
  CollapsedEventNameWrapper,
  EventKeyWrapper,
  EventRightWrapper,
  EventLeftWrapper,
  RuleText,
  ANDText,
  EventItemActionsWrapper,
} from '@src/emp-3.0/components/Metrics/Flow/FlowForm/styled';
import Icon, { DownOutlined, CloseOutlined, RightOutlined } from '@ant-design/icons';
import EditSvg from '@icons/event-management/edit.svg';
import WarnSvg from '@icons/warning.svg';
import CloneSvg from '@icons/emp/clone.svg';
import { formatOperator } from '@src/emp-3.0/utils';
import { isViewMode } from '@src/pages/metric-templates/utils';
import { Button, Popover, Tooltip } from 'antd';
import { useWorkflowStore } from '@src/pages/workflow/store';
import { mappedEventDeployedStatus } from '@src/common/constant/enum';
import { PopupContainer } from '@src/emp-3.0/components/MappedEventBuilder/styled';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';

const ReadMode = ({ expanded, event, onEdit, onDelete, onClone }) => {
  const { formMode } = useWorkflowStore();
  const isView = isViewMode(formMode);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [isExpanded, setExpanded] = useState<boolean>(expanded);
  const { metadata: metadataList } = useInitialInfoStore();
  const hasRelatedFlows = event?.related_flows?.length > 0;
  const hasRelatedMetrics = event?.related_metrics?.length > 0;
  const shouldShowPopup =
    (hasRelatedFlows || hasRelatedMetrics || event.status === mappedEventDeployedStatus.deployed) &&
    !event?.is_predefined;
  const editButton = (
    <Tooltip
      title={
        event?.is_predefined
          ? 'Conviva pre-defined events cannot be edited. Clone first to make changes.'
          : 'Edit'
      }
    >
      <Button
        disabled={event?.is_predefined}
        type={'text'}
        style={{ color: 'rgba(0, 0, 0, 0.45)' }}
        icon={<Icon component={EditSvg} style={{ fontSize: '12px' }} />}
        onClick={(e) => {
          e.stopPropagation();
          if (shouldShowPopup) {
            setShowPopup(true);
            return;
          }
          setExpanded(true);
          onEdit?.(event);
        }}
      />
    </Tooltip>
  );

  const warnText =
    hasRelatedFlows || hasRelatedMetrics
      ? 'Changes to this event will impact other flows and metrics.'
      : ' This is a deployed event. Changes will impact your analysis going forward.';

  const popupContent = (
    <PopupContainer onClick={(e) => e.stopPropagation()}>
      <div className="popup-header">
        <span style={{ fontWeight: 400 }}>
          <Icon component={WarnSvg} style={{ fontSize: '12px', marginRight: '8px' }} /> {warnText}
        </span>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            setShowPopup(false);
          }}
        />
      </div>
      {hasRelatedFlows && (
        <div className="popup-content-item">
          <div>Impacted Flows:</div>
          <div>
            {event?.related_flows?.map((item, index) => {
              return <div key={item + index}>{item}</div>;
            })}
          </div>
        </div>
      )}
      {hasRelatedMetrics && (
        <div className="popup-content-item">
          <div>Impacted Metrics:</div>
          <div>
            {event?.related_metrics?.map((item, index) => {
              return <div key={item + index}>{item}</div>;
            })}
          </div>
        </div>
      )}
      <div className="popup-footer">
        <div style={{ display: 'flex', gap: '10px' }}>
          <Button
            type="primary"
            ghost
            onClick={() => {
              setExpanded(true);
              onClone?.(event);
            }}
          >
            Clone As New Event
          </Button>
          <Button
            onClick={() => {
              setShowPopup(false);
              setExpanded(true);
              onEdit?.(event);
            }}
          >
            Update Current Event
          </Button>
        </div>
      </div>
    </PopupContainer>
  );

  const getEventName = () => (
    <CollapsedEventNameWrapper
      onClick={() => {
        setExpanded(!isExpanded);
      }}
    >
      <div style={{ textAlign: 'left', paddingRight: '5px', display: 'flex' }}>
        {isExpanded ? (
          <DownOutlined style={{ color: 'rgb(0,0,0,0.45)' }} />
        ) : (
          <RightOutlined style={{ color: 'rgb(0,0,0,0.45)' }} />
        )}
        <div style={{ paddingLeft: '8px', fontWeight: '500' }}>Name:</div>
        <div style={{ paddingLeft: '8px' }}>{event?.event_name}</div>
      </div>
      {isView ? null : (
        <EventItemActionsWrapper
          className="event-item-actions"
          style={showPopup ? { display: 'flex' } : undefined}
        >
          {shouldShowPopup ? (
            <Popover
              content={popupContent}
              open={showPopup}
              trigger="click"
              onOpenChange={(v) => setShowPopup(v)}
              getPopupContainer={() => document.body}
            >
              {editButton}
            </Popover>
          ) : (
            editButton
          )}
          {event?.rules?.length ? (
            <Tooltip title={'Clone'}>
              <Button
                type={'text'}
                style={{ color: 'rgba(0, 0, 0, 0.45)' }}
                icon={<Icon component={CloneSvg} style={{ fontSize: '12px' }} />}
                onClick={(e) => {
                  setExpanded(true);
                  onClone?.(event);
                  e.stopPropagation();
                }}
              />
            </Tooltip>
          ) : null}
          <div
            style={{ width: '1px', height: '16px', background: '#D9D9D9', margin: '0 4px' }}
          ></div>
          <Button
            type={'text'}
            style={{ color: 'rgba(0, 0, 0, 0.45)' }}
            icon={<Icon component={DeleteSvg} style={{ fontSize: '12px' }} />}
            onClick={(e) => {
              onDelete?.();
              e.stopPropagation();
            }}
          />
        </EventItemActionsWrapper>
      )}
    </CollapsedEventNameWrapper>
  );

  const getMetadataName = (metadataKey) => {
    const metadataName = metadataList.find(
      (dimension) => dimension?.dimension_name === metadataKey,
    )?.display_name;
    return metadataName || metadataKey || 'Metadata Name';
  };

  return (
    <EventWrapper>
      {getEventName()}
      {isExpanded && (
        <div style={{ width: '100%', padding: '0 24px 0 30px', borderTop: 'solid 1px #d9d9d9' }}>
          <EventKeyWrapper>
            <EventLeftWrapper className="event-left" style={{ fontWeight: '500' }}>
              Event
            </EventLeftWrapper>
            <EventRightWrapper style={{ fontWeight: '500' }}>Tag Rule</EventRightWrapper>
          </EventKeyWrapper>
          {Array.isArray(event?.rules) &&
            event?.rules?.map((rowRules, rowIndex) => {
              return rowRules?.map((rule, ruleIndex) => {
                return (
                  <>
                    {ruleIndex !== 0 && (
                      <div style={{ borderBottom: 'solid 1px #d9d9d9', margin: '3px' }} />
                    )}
                    <EventKeyWrapper>
                      <EventLeftWrapper className="event-left" style={{ marginBottom: '10px' }}>
                        {ruleIndex !== 0 ? (
                          <RuleText
                            style={{
                              color: 'rgba(0, 0, 0, 0.45)',
                              textAlign: 'left',
                              marginRight: '10px',
                            }}
                          >
                            {'OR'}
                          </RuleText>
                        ) : null}
                        {rule?.value?.join()}
                      </EventLeftWrapper>
                      <EventRightWrapper>
                        {rule?.mapping_rules?.map((rowMappingRule, rowMappingIndex) => {
                          return (
                            <React.Fragment key={rowMappingIndex}>
                              {rowMappingIndex !== 0 && (
                                <div
                                  style={{
                                    display: 'flex',
                                    padding: '10px 0px',
                                    alignItems: 'center',
                                  }}
                                >
                                  <div
                                    style={{
                                      width: '77px',
                                      height: '2px',
                                      background: 'rgba(0, 0, 0, 0.06)',
                                    }}
                                  />
                                  <ANDText>AND</ANDText>
                                  <div
                                    style={{
                                      width: '180px',
                                      height: '2px',
                                      background: 'rgba(0, 0, 0, 0.06)',
                                    }}
                                  />
                                </div>
                              )}
                              {rowMappingRule?.map((rule, ruleIndex) => (
                                <div
                                  key={ruleIndex}
                                  style={{ display: 'flex', paddingBottom: '10px', width: '100%' }}
                                >
                                  {ruleIndex === 0 ? null : (
                                    <RuleText
                                      width={'30px'}
                                      style={{
                                        color: 'rgba(0, 0, 0, 0.45)',
                                        minWidth: '30px',
                                        marginLeft: '-30px',
                                      }}
                                    >
                                      {'OR'}
                                    </RuleText>
                                  )}

                                  <RuleText width={ruleIndex === 0 ? '100%' : 'calc(100% - 30px)'}>
                                    <span style={{ paddingRight: '10px' }}>
                                      {rule?.tag_key?.value.join()}
                                    </span>
                                    {rule?.tag_key?.value.join() && (
                                      <span
                                        style={{
                                          paddingRight: '10px',
                                          color: 'rgba(0, 0, 0, 0.45)',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {formatOperator(rule?.tag_value?.op)}
                                      </span>
                                    )}
                                    <span style={{ paddingRight: '10px' }}>
                                      {rule?.tag_value?.value.join()}
                                    </span>
                                  </RuleText>
                                </div>
                              ))}
                            </React.Fragment>
                          );
                        })}
                        {rule?.metadata?.map((metadataItem, metadataIndex) => {
                          return (
                            <React.Fragment key={metadataIndex}>
                              {!(metadataIndex === 0 && !rule?.mapping_rules.length) && (
                                <div
                                  style={{
                                    display: 'flex',
                                    padding: '10px 0px',
                                    alignItems: 'center',
                                  }}
                                >
                                  <div
                                    style={{
                                      width: '77px',
                                      height: '2px',
                                      background: 'rgba(0, 0, 0, 0.06)',
                                    }}
                                  />
                                  <ANDText>AND</ANDText>
                                  <div
                                    style={{
                                      width: '180px',
                                      height: '2px',
                                      background: 'rgba(0, 0, 0, 0.06)',
                                    }}
                                  />
                                </div>
                              )}
                              {metadataItem?.map((rule, ruleIndex) => (
                                <div
                                  key={ruleIndex}
                                  style={{ display: 'flex', paddingBottom: '10px', width: '100%' }}
                                >
                                  {ruleIndex === 0 ? null : (
                                    <RuleText
                                      width={'30px'}
                                      style={{
                                        color: 'rgba(0, 0, 0, 0.45)',
                                        minWidth: '30px',
                                        marginLeft: '-30px',
                                      }}
                                    >
                                      {'OR'}
                                    </RuleText>
                                  )}

                                  <RuleText width={ruleIndex === 0 ? '100%' : 'calc(100% - 30px)'}>
                                    <span style={{ paddingRight: '10px' }}>
                                      {getMetadataName(rule?.key)}
                                    </span>
                                    {rule?.value.join() && (
                                      <span
                                        style={{
                                          paddingRight: '10px',
                                          color: 'rgba(0, 0, 0, 0.45)',
                                        }}
                                      >
                                        {formatOperator(rule?.op)}
                                      </span>
                                    )}
                                    <span style={{ paddingRight: '10px' }}>
                                      {rule?.value.join()}
                                    </span>
                                  </RuleText>
                                </div>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </EventRightWrapper>
                    </EventKeyWrapper>
                  </>
                );
              });
            })}
        </div>
      )}
    </EventWrapper>
  );
};

export default ReadMode;
